// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import moment from 'moment';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Collapsible from './../../components/Collapsible';
import PrintReceiptLink from './../../components/PrintReceiptLink';
import ServiceRequest from './../../components/ServiceRequest';
import ReActivateLink from './../../components/ReActivateLink';
import { userAgentTierSelector } from './../../store/user';
import {
    IServiceRequest,
    IClaimOrder,
} from './../../businessLogic/sagas/serviceRequest/types';

import Styles from './claimOrder.module.scss';
import onDemandEnable from './../../lib/OnDemandEnableConfig';

interface PropsType {
    data: IClaimOrder;
}

export default function ClaimOrder(props: PropsType) {
    const { t } = useTranslation();
    return (
        <Collapsible
            colorOnOpen={'alabaster'}
            colorOnClose={'alabaster'}
            chevronColor={'black'}
            header={ClaimOrderHeader(props.data)}
            className={Styles.claimOrder}
            tableName={t('claim_order_number')}
        >
            <div className={Styles.serviceRequests}>
                {props.data.serviceRequests && props.data.serviceRequests.map(
                    (serviceRequest: IServiceRequest, key: number) => props.data.status.toUpperCase() === 'REACTIVATE' && serviceRequest.serviceRequestNumber === 0 ? null : <ServiceRequest
                        agentTier={props.data.tier}
                        claimId={props.data.claimOrderId}
                        data={serviceRequest}
                        isOpen={props.data.serviceRequests.length === 1}
                        key={key}
                        reasonForNoRepair={props.data.reasonForNoRepair ? props.data.reasonForNoRepair : ''}
                        reasonForNoRepairDesc={props.data.reasonForNoRepairDescription ? props.data.reasonForNoRepairDescription : ''}
                        claimOrderId={props.data.claimOrderId}
                        serviceLocationDetails={props.data.serviceLocationDetails}
                        countryCode={props.data.shipments?.shipTo?.country}
                    />
                )}
            </div>
        </Collapsible>
    );
}

function ClaimOrderHeader(data: IClaimOrder) {
    const { t } = useTranslation();
    const createdDate = moment(data.createdDate).format('ll');
    const userAgentTier = useSelector(state => userAgentTierSelector(state));
    return (
        <div className={Styles.header}>
            <div className={Styles.order}>
                <div className={Styles.orderNumberLabel}>{t('claim_order_number')}:</div>
                <div className={Styles.orderNumber}>{data.claimOrderId}</div>
            </div>
            <div className={Styles.details}>
                <div>{t('order_created_date', { date: createdDate })}</div>
                <div className={Styles.detail}>
                    <PrintReceiptLink claim={data} />
                </div>
            </div>
            <div className={Styles.status}>
                <div className={Styles.statusLabel}>{t('status')}:</div>
                {data.status?.toUpperCase() === 'OPEN' && <Icon className={Styles.statusIcon} iconName="CheckMark" />}
                {(data.status?.toUpperCase() === t('Reactivate').toUpperCase()
                || data.status?.toUpperCase() === 'REACTIVATE')
                && onDemandEnable.onDemandEnableTiers.includes(userAgentTier) ?  <ReActivateLink claim={data} />
                : <div className={Styles.statusState}>{t(data.status)}</div> }
            </div>
        </div>
    );
}